import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { trackEvent } from "@/utils/analytics";

export function Logo() {
  return (
    <Link to="/" className="flex items-center" data-testid="logo-link">
      <img
        src="/logo-masthead.png"
        alt="HairHelp — Real Experience. Real Solutions."
        className="h-16 w-auto md:h-20"
        data-testid="logo-image"
      />
    </Link>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const base = "font-manrope text-[15px] font-semibold transition-colors duration-200";
  const linkClass = ({ isActive }, home) =>
    `${base} text-white hover:text-white/80`;

  return (
    <>
    <header className="bg-black" data-testid="site-navbar">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-2 md:px-6 md:py-2.5">
        <Link to="/" className="block shrink-0" data-testid="logo-link-header">
          <img
            src="/logo-masthead.png"
            alt="HairHelp — Real Experience. Real Solutions."
            className="h-[124px] w-auto rounded-2xl md:h-[180px]"
            data-testid="logo-image-header"
          />
        </Link>
        <nav className="hidden items-center gap-6 md:flex">
          <NavLink to="/" end className={linkClass} data-testid="nav-home-link">Home</NavLink>
          <Link
            to="/fai-una-domanda"
            data-testid="nav-ask-cta"
            onClick={() => trackEvent("cta_consiglio_gratuito", { location: "nav" })}
            className="rounded-full bg-sage px-5 py-2.5 font-manrope text-sm font-semibold text-white shadow-[0_6px_16px_rgba(63,90,82,0.35)] transition-all duration-300 hover:bg-[#33463f] active:scale-95"
          >
            Consiglio gratuito
          </Link>
          <NavLink to="/chi-siamo" end className={linkClass} data-testid="nav-about-link">Chi siamo</NavLink>
        </nav>
        <button
          className="-my-2 p-2 text-ivory md:hidden"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-ivory/10 bg-charcoal px-5 pb-5 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1 pt-2">
            <NavLink to="/" end onClick={() => setOpen(false)} data-testid="mobile-nav-home-link" className="min-h-[48px] py-3 font-manrope text-base font-semibold text-white">
              Home
            </NavLink>
            <Link
              to="/fai-una-domanda"
              onClick={() => { setOpen(false); trackEvent("cta_consiglio_gratuito", { location: "nav_mobile" }); }}
              data-testid="mobile-nav-ask-cta"
              className="my-1 inline-flex min-h-[48px] w-full items-center justify-center rounded-full bg-sage px-5 py-3 font-manrope text-sm font-semibold text-white"
            >
              Consiglio gratuito
            </Link>
            <NavLink to="/chi-siamo" end onClick={() => setOpen(false)} data-testid="mobile-nav-about-link" className="min-h-[48px] py-3 font-manrope text-base font-semibold text-white">
              Chi siamo
            </NavLink>
          </div>
        </div>
      )}
    </header>
    <div className="h-8 bg-gradient-to-b from-black to-transparent md:h-10" data-testid="header-fade" />
    </>
  );
}

export function Footer() {
  const col = "inline-block py-2 text-ivory/70 transition-colors duration-200 hover:text-ivory";
  const head = "text-xs font-bold uppercase tracking-widest text-terracotta";
  return (
    <footer className="bg-charcoal text-ivory" data-testid="footer">
      <div className="mx-auto grid max-w-6xl gap-10 px-6 py-14 md:grid-cols-4">
        <div>
          <Link to="/" className="inline-flex items-center" data-testid="footer-logo-link">
            <img src="/logo-masthead.png" alt="HairHelp" className="h-16 w-auto rounded-2xl" />
          </Link>
          <p className="mt-4 max-w-xs font-manrope text-sm leading-relaxed text-ivory/55">
            Esperienza, competenza, consiglio, soluzione. Il punto di riferimento per chi utilizza sistemi capillari.
          </p>
        </div>
        <div className="flex flex-col gap-2.5 font-manrope text-sm">
          <span className={head}>Navigazione</span>
          <Link to="/" className={col} data-testid="footer-home-link">Home</Link>
          <Link to="/fai-una-domanda" className={col} data-testid="footer-ask-link">Consiglio gratuito</Link>
          <Link to="/chi-siamo" className={col} data-testid="footer-about-link">Chi siamo</Link>
          <Link to="/contatti" className={col} data-testid="footer-contact-link">Contatti</Link>
        </div>
        <div className="flex flex-col gap-2.5 font-manrope text-sm">
          <span className={head}>Contatti</span>
          <a href="mailto:admin@hairhelp.studio" className={col} data-testid="footer-admin-email-link">admin@hairhelp.studio</a>
        </div>
        <div className="flex flex-col gap-2.5 font-manrope text-sm">
          <span className={head}>Legale</span>
          <Link to="/privacy" className={col} data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/termini" className={col} data-testid="footer-terms-link">Termini di utilizzo</Link>
          <Link to="/cookie-policy" className={col} data-testid="footer-cookie-link">Cookie Policy</Link>
        </div>
      </div>
      <div className="border-t border-ivory/10 px-6 py-5 text-center font-manrope text-xs text-ivory/40">
        © {new Date().getFullYear()} HairHelp — hairhelp.studio. Consigli pratici basati sull&rsquo;esperienza, non pareri medici.
      </div>
    </footer>
  );
}
